/**
 * @module components/issues/IssuesFilterBar
 *
 * The issues toolbar lockup, mirroring the flows toolbar: a flush search
 * well, the three most-used filters (Status / Severity / Category) as
 * fixed-size menus, the "More filters" dialog trigger (last seen, credentials,
 * tags, archived — see `IssuesFilters.tsx`), a Clear action, and the active
 * filter chips row. Built from the shared `@chatsdet/ui` FilterToolbar
 * primitives so flows and issues stay visually in lockstep.
 */
import React from 'react';
import {
  Button,
  FilterToolbar,
  FilterToolbarChips,
  FilterToolbarDivider,
  FilterToolbarSearch,
  ToolbarFilterMenu,
  type FilterToolbarChip,
  type MultiSelectOption,
} from '@chatsdet/ui';
import type { IssueSeverity, IssueStatus } from '@chatsdet/types/issues';
import {
  SEVERITY_OPTIONS,
  STATUS_OPTIONS,
  type IssuesFiltersSearchParams,
} from './IssuesFilters';

export type IssuesFilterBarProps = {
  searchInput: string;
  onSearchInputChange: (value: string) => void;

  statusFilter: IssueStatus[];
  severityFilter: IssueSeverity | 'all';
  categoryFilter: string[];
  categoryOptions: MultiSelectOption[];
  categoriesLoading?: boolean;

  onFilterChange: (updates: Partial<IssuesFiltersSearchParams>) => void;

  /** "More filters" dialog trigger from IssuesFilters. */
  filterDialogTrigger: React.ReactNode;
  /** Every active filter as a little removable tag, shown in a row inside the lockup. */
  activeFilterChips: FilterToolbarChip[];
  onChipRemove: (chipId: string) => void;
  /** Clear search + every filter. Hidden when nothing is active. */
  onClearAll: () => void;
  showClearAll: boolean;
  /** Fuse with the table card below: square bottom corners, no bottom border. */
  attached?: boolean;
};

export function IssuesFilterBar({
  searchInput,
  onSearchInputChange,
  statusFilter,
  severityFilter,
  categoryFilter,
  categoryOptions,
  categoriesLoading,
  onFilterChange,
  filterDialogTrigger,
  activeFilterChips,
  onChipRemove,
  onClearAll,
  showClearAll,
  attached = false,
}: IssuesFilterBarProps) {
  // The URL keeps an 'all' sentinel when the user cleared every status (the
  // empty list would otherwise fall back to the default 'open' filter).
  const selectedStatuses = React.useMemo(
    () => statusFilter.filter((status) => (status as string) !== 'all'),
    [statusFilter]
  );

  const handleStatusChange = React.useCallback(
    (ids: string[]) => {
      onFilterChange({
        statuses:
          ids.length > 0 ? (ids as IssueStatus[]) : (['all'] as unknown as IssueStatus[]),
        page: 1,
      });
    },
    [onFilterChange]
  );

  const handleSeverityChange = React.useCallback(
    (ids: string[]) => {
      onFilterChange({ severity: (ids[0] as IssueSeverity | undefined) ?? 'all', page: 1 });
    },
    [onFilterChange]
  );

  const handleCategoryChange = React.useCallback(
    (ids: string[]) => {
      onFilterChange({ categoryIds: ids.length > 0 ? ids : undefined, page: 1 });
    },
    [onFilterChange]
  );

  return (
    <FilterToolbar attached={attached}>
      <FilterToolbarSearch
        aria-label="Search issues"
        placeholder="Search by title or url..."
        value={searchInput}
        onChange={onSearchInputChange}
      />

      <ToolbarFilterMenu
        label="Status"
        options={STATUS_OPTIONS}
        selectedIds={selectedStatuses}
        onChange={handleStatusChange}
      />
      <ToolbarFilterMenu
        label="Severity"
        selectionMode="single"
        options={SEVERITY_OPTIONS}
        selectedIds={severityFilter === 'all' ? [] : [severityFilter]}
        onChange={handleSeverityChange}
      />
      <ToolbarFilterMenu
        label="Category"
        options={categoryOptions}
        selectedIds={categoryFilter}
        onChange={handleCategoryChange}
        isLoading={categoriesLoading}
      />

      <FilterToolbarDivider />

      {filterDialogTrigger}

      {showClearAll && (
        <Button variant="ghost" size="sm" className="text-muted-foreground" onPress={onClearAll}>
          Clear
        </Button>
      )}

      <FilterToolbarChips chips={activeFilterChips} onRemove={onChipRemove} />
    </FilterToolbar>
  );
}
